import type { PropsWithChildren } from 'react';

import { DAVIDS_BRIDAL_FILTER_LABEL, DAVIDS_BRIDAL_FILTER_SLUGS, TieTypes } from 'constants/collections';
import Filter from 'components/Filter';
import productCategories, { CategoryDto } from 'components/Collection/pages/categories';
import visualizer from 'partials/Visualizer';
import type { ColorFilter } from 'partials/Visualizer';
import { Item, ProductCategoryFilter } from 'types';
import { capitalizeStringAsTitle } from 'utils/utils';

type FilterGroupProps = PropsWithChildren<
  {
    headerText: string;
  } & DataTestIdProp
>;

const FilterGroup = ({ children, headerText, ...props }: FilterGroupProps) => (
  <div className="space-y-16" data-testid={props['data-testid']}>
    <div>
      <h3 className="text-h6">{headerText}</h3>
    </div>
    <div className="grid grid-cols-3 gap-8 xs:grid-cols-1">{children}</div>
    <hr className="border-gray-light" />
  </div>
);

export type FilterGroupName = 'category' | 'tieType' | 'pattern' | 'color' | 'procurement' | 'davidsBridal';

export type FilterComponentProps = {
  activeCategory: CategoryDto;
  categoryFilters: ProductCategoryFilter[];
  colorFilters: ColorFilter[];
  davidsBridalFilters: string[];
  patternFilters: string[];
  procurementFilters: string[];
  products: Item[];
  tieTypeFilters: string[];
  toggleCategoryFilters: (category: ProductCategoryFilter) => void;
  toggleColorFilters: (color: ColorFilter) => void;
  toggleDavidsBridalFilters: (davidsBridalFilter: string) => void;
  togglePatternFilters: (pattern: string) => void;
  toggleProcurementFilters: (procurementType: string) => void;
  toggleTieTypeFilters: (tieTypeFilter: string) => void;
  visibleFilters?: FilterGroupName[];
};

const FilterComponent = (props: FilterComponentProps) => {
  const { activeCategory, visibleFilters } = props;

  const isFilterGroupVisible = (filterName: FilterGroupName): boolean => {
    return visibleFilters === undefined || visibleFilters.includes(filterName);
  };

  const buildCategoryFilters = () => {
    const { categoryFilters, activeCategory, toggleCategoryFilters, products } = props;

    return productCategories
      .find((c) => c.name === activeCategory.name)
      ?.categoryFilters.map((filter, key) => {
        const product = products.find((p) => {
          if (p.category === 'Tie') {
            return p.display_name?.includes(filter.value as string) || filter.displayName === 'Long';
          } else {
            /**
             * @todo come back to this and figure out if we can compare against the type of filter.value
             */
            return p.display_name?.concat(p.category!).includes(filter.value as string) || filter.displayName === 'Dress Shirts';
          }
        });

        if (product) {
          return (
            <Filter
              id={`${String(filter.displayName).replace(' ', '')}FilterBtn`}
              key={key}
              onClick={() => {
                toggleCategoryFilters(filter);
              }}
              isActive={categoryFilters.includes(filter)}
              filterLabel={filter.displayName}
            />
          );
        }
      });
  };

  const buildTieTypeFilters = () => {
    const { tieTypeFilters, toggleTieTypeFilters } = props;

    // Tie Types filter should only visible under Ties category
    if (activeCategory.name !== 'Tie') {
      return null;
    }

    return TieTypes.map((filterName) => (
      <Filter
        id={`${String(filterName).replace(' ', '')}FilterBtn`}
        key={filterName}
        onClick={() => {
          toggleTieTypeFilters(filterName);
        }}
        isActive={tieTypeFilters.includes(filterName)}
        filterLabel={filterName}
      />
    ));
  };

  const buildProcurementFilters = () => {
    const { procurementFilters, toggleProcurementFilters, products } = props;

    const procurementTypes = products.map((product) =>
      product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase' ? 'Buy' : 'Rent',
    );

    const uniqueProcurementTypes = [...new Set(procurementTypes)];

    return uniqueProcurementTypes.map((filterName) => (
      <Filter
        id={`${String(filterName).replace(' ', '')}FilterBtn`}
        key={filterName}
        onClick={() => {
          toggleProcurementFilters(filterName);
        }}
        isActive={procurementFilters.includes(filterName)}
        filterLabel={filterName}
      />
    ));
  };

  const buildColorFilters = () => {
    const { colorFilters, toggleColorFilters, products } = props;

    const productColors = products.map((product) => product.color).filter((c) => Boolean(c));
    const orderedProductColors = visualizer.getOrderedProductColors(productColors as ColorFilter[]);

    return orderedProductColors.map((colorFilter) => (
      <Filter
        id={`${colorFilter}FilterBtn`}
        key={colorFilter}
        onClick={() => toggleColorFilters(colorFilter as ColorFilter)}
        isActive={colorFilters.includes(colorFilter as ColorFilter)}
        filterColor={colorFilter}
        filterLabel={capitalizeStringAsTitle(colorFilter)}
      />
    ));
  };

  const buildPatternFilters = () => {
    const { patternFilters, togglePatternFilters, products } = props;

    const patterns = products.reduce(
      (patterns, product) => (product.pattern ? patterns.concat(product.pattern) : patterns),
      [] as string[],
    );

    if (patterns.length > 0) {
      return [...new Set(patterns)].map((filterName) => {
        return (
          <Filter
            id={`${String(filterName).replace(' ', '')}FilterBtn`}
            key={filterName}
            onClick={() => {
              togglePatternFilters(filterName);
            }}
            isActive={patternFilters.includes(filterName)}
            filterLabel={filterName}
          />
        );
      });
    }
    return [];
  };

  const buildDavidsBridalFilters = () => {
    const { davidsBridalFilters, toggleDavidsBridalFilters } = props;

    // David's Bridal Colors filter is only available on the Ties and Accessories collection pages.
    if (!activeCategory.slug || !DAVIDS_BRIDAL_FILTER_SLUGS.includes(activeCategory.slug)) {
      return null;
    }

    return (
      <Filter
        id="DavidsBridalMatchesFilterBtn"
        onClick={() => {
          toggleDavidsBridalFilters(DAVIDS_BRIDAL_FILTER_LABEL);
        }}
        isActive={davidsBridalFilters.includes(DAVIDS_BRIDAL_FILTER_LABEL)}
        filterLabel={DAVIDS_BRIDAL_FILTER_LABEL}
      />
    );
  };

  const categories = activeCategory.name === '' ? [] : buildCategoryFilters();
  const tieTypes = activeCategory.name === '' ? [] : buildTieTypeFilters();
  const patterns = activeCategory.name === '' ? [] : buildPatternFilters();
  const colors = activeCategory.name === '' ? [] : buildColorFilters();
  const procurementTypes = activeCategory.name === '' ? [] : buildProcurementFilters();
  const davidsBridal = activeCategory.name === '' ? null : buildDavidsBridalFilters();

  const getSelectedFilterCount = () =>
    props.categoryFilters.length +
    props.colorFilters.length +
    props.davidsBridalFilters.length +
    props.patternFilters.length +
    props.procurementFilters.length +
    props.tieTypeFilters.length;

  return (
    <div className="space-y-16">
      <div mb={3}>
        <h2 className="text-h5">Filters {getSelectedFilterCount() > 0 && `(${getSelectedFilterCount()})`}</h2>
      </div>
      <hr className="border-gray-light" />

      {isFilterGroupVisible('category') && categories && categories.length > 0 && (
        <FilterGroup data-testid="filter-categories" headerText="Category">
          {categories}
        </FilterGroup>
      )}

      {isFilterGroupVisible('tieType') && tieTypes && tieTypes.length > 0 && (
        <FilterGroup data-testid={'filter-tie-types'} headerText="Tie Types">
          {tieTypes}
        </FilterGroup>
      )}

      {isFilterGroupVisible('pattern') && patterns.length > 0 && (
        <FilterGroup data-testid={'filter-patterns'} headerText="Pattern">
          {patterns}
        </FilterGroup>
      )}

      {isFilterGroupVisible('color') && colors.length > 0 && (
        <FilterGroup data-testid={'filter-colors'} headerText="Colors">
          {colors}
        </FilterGroup>
      )}

      {isFilterGroupVisible('davidsBridal') && davidsBridal && (
        <FilterGroup data-testid={'filter-davids-bridal'} headerText="David's Bridal Colors">
          {davidsBridal}
        </FilterGroup>
      )}

      {isFilterGroupVisible('procurement') && procurementTypes && procurementTypes.length > 0 && (
        <FilterGroup data-testid={'filter-procurements'} headerText="Type">
          {procurementTypes}
        </FilterGroup>
      )}
    </div>
  );
};

export default FilterComponent;
